import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import axios from "axios";
import {
  alreadySavedReportId,
  useDeleteReport,
  useSaveReport,
  useUnscheduleReport
} from "@/entities/savedReports/hooks";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import { useChat } from "@/components/layouts/Chat/ChatContext";
import { useChatTabs } from "@/components/global/Chat/components/chat/ChatTabsContext";
import { useReportRuns } from "@/components/global/Chat/components/dreamerReports/ReportRunsContext";
import { ReportSaveControl } from "@/components/global/Chat/components/dreamerReports/ReportSaveControl";
import { TRANSLATION_KEY } from "@/components/global/Chat/components/dreamerReports/consts";
import { ScheduleData } from "@/components/global/Chat/components/ScheduleModal/types";

interface UseChatReportSaveParams {
  messageId: string;
  reportName: string;
  /** What the server computed for this answer when the message arrived. */
  savedReportId: string | null;
  isScheduled: boolean;
  /** The answer's cadence, which the scheduled control describes. */
  scheduleConfig?: ScheduleData | null;
  /** Whether the answer can be replayed, which saving it depends on. */
  isSchedulable: boolean;
  /**
   * Opens the schedule settings for this answer. On an unsaved answer that is
   * Save & Schedule: confirming the settings saves and schedules in one write,
   * and the caller reports the new row back through `markScheduled`.
   */
  onSchedule: () => void;
  /**
   * Called once the answer's schedule is gone — removed on its own, or with the
   * report — so the caller stops showing it.
   */
  onScheduleCleared: () => void;
}

/**
 * The saved state of one chat answer, and the writes that change it.
 *
 * The message carries `savedReportId` as it was when it arrived; after that
 * the answer's own saves and removals are the truth. They override it locally,
 * so the control flips at once, and are written back onto the chat's copy of
 * the message, so an answer remounted later — a tab switch — still has them.
 *
 * `saveControl` is undefined while the My Reports flag is off, which leaves the
 * card on its legacy schedule button — and with it the legacy state: the
 * message's own `savedReportId` passes through untouched, since a legacy
 * cancel deletes the row a recorded save would still point at.
 */
export const useChatReportSave = ({
  messageId,
  reportName,
  savedReportId,
  isScheduled,
  scheduleConfig = null,
  isSchedulable,
  onSchedule,
  onScheduleCleared
}: UseChatReportSaveParams) => {
  const { t } = useTranslation();
  const { enabled: isMyReportsEnabled } = useFeatureFlag(
    FeatureFlags.CRM_10285_MY_REPORTS
  );
  const { showTab } = useChatTabs();
  const { updateMessageReport, forgetSavedReport } = useChat();
  const { forgetReportRun } = useReportRuns();
  const [localSavedReportId, setLocalSavedReportId] = useState<
    string | null | undefined
  >(undefined);
  // The override bridges the render between a write and the message catching
  // up. Once the message changes — this answer's write landing, or another
  // surface removing the report — the message is the truth again.
  useEffect(() => setLocalSavedReportId(undefined), [savedReportId]);
  const effectiveSavedReportId =
    isMyReportsEnabled && localSavedReportId !== undefined
      ? localSavedReportId
      : savedReportId;

  const saveReport = useSaveReport();
  const deleteReport = useDeleteReport();
  const unscheduleReport = useUnscheduleReport();

  /** A first schedule on an unsaved answer is also its save. */
  const markScheduled = useCallback(
    (id: string) => {
      if (!isMyReportsEnabled) return;
      setLocalSavedReportId(id);
      updateMessageReport(messageId, { savedReportId: id, scheduleId: id });
    },
    [isMyReportsEnabled, messageId, updateMessageReport]
  );

  /** The cadence went and the report stayed. */
  const markUnscheduled = useCallback(() => {
    if (!isMyReportsEnabled) return;
    updateMessageReport(messageId, { scheduleId: null });
  }, [isMyReportsEnabled, messageId, updateMessageReport]);

  const save = useCallback(() => {
    const onSaved = (id: string) => {
      setLocalSavedReportId(id);
      updateMessageReport(messageId, { savedReportId: id });
      toast.success(t(`${TRANSLATION_KEY}.save.done`));
    };
    saveReport.mutate(
      { data: { messageId, name: reportName } },
      {
        onSuccess: (created) => onSaved(created.id),
        onError: (error) => {
          // Saved already — from another tab, or before this message
          // refreshed. The refusal names the row, which is the outcome the
          // user asked for.
          const existingId = alreadySavedReportId(error);
          if (existingId) onSaved(existingId);
          else toast.error(t(`${TRANSLATION_KEY}.save.failed`));
        }
      }
    );
  }, [saveReport, messageId, reportName, updateMessageReport, t]);

  const remove = useCallback(() => {
    if (!effectiveSavedReportId) return;
    const onRemoved = () => {
      setLocalSavedReportId(null);
      forgetSavedReport(effectiveSavedReportId);
      forgetReportRun(effectiveSavedReportId);
      onScheduleCleared();
      toast.success(t(`${TRANSLATION_KEY}.delete.done`));
    };
    deleteReport.mutate(
      { pathParams: { id: effectiveSavedReportId } },
      {
        onSuccess: onRemoved,
        onError: (error) => {
          // Removed already — from the Reports tab, another tab, or a
          // repeated confirm. The report is gone, which is what was asked.
          const isGone =
            axios.isAxiosError(error) && error.response?.status === 404;
          if (isGone) onRemoved();
          else toast.error(t(`${TRANSLATION_KEY}.delete.failed`));
        }
      }
    );
  }, [
    deleteReport,
    effectiveSavedReportId,
    forgetSavedReport,
    forgetReportRun,
    onScheduleCleared,
    t
  ]);

  // The cadence goes and the report stays: saving and scheduling are one row,
  // so this clears the cadence on it rather than deleting it.
  const removeSchedule = useCallback(() => {
    if (!effectiveSavedReportId) return;
    unscheduleReport.mutate(
      {
        pathParams: { id: effectiveSavedReportId },
        data: { scheduleConfig: null }
      },
      {
        onSuccess: () => {
          markUnscheduled();
          onScheduleCleared();
          toast.success(t(`${TRANSLATION_KEY}.removeSchedule.done`));
        },
        onError: () =>
          toast.error(t(`${TRANSLATION_KEY}.removeSchedule.failed`))
      }
    );
  }, [
    unscheduleReport,
    effectiveSavedReportId,
    markUnscheduled,
    onScheduleCleared,
    t
  ]);

  const isSaved = !!effectiveSavedReportId;
  // The card and an expanded view of it each place their own copy; the view's
  // menu opens leftwards and leaves out Manage reports.
  const renderSaveControl = ({
    align,
    inReportView
  }: { align?: "start" | "end"; inReportView?: boolean } = {}) =>
    isMyReportsEnabled && (isSaved || isSchedulable) ? (
      <ReportSaveControl
        isSaved={isSaved}
        isScheduled={isScheduled}
        scheduleConfig={scheduleConfig}
        reportName={reportName}
        isSaving={
          saveReport.isPending ||
          deleteReport.isPending ||
          unscheduleReport.isPending
        }
        align={align}
        inReportView={inReportView}
        onSave={save}
        onSaveAndSchedule={onSchedule}
        onSchedule={onSchedule}
        onManageReports={() => showTab("reports")}
        onRemoveSchedule={removeSchedule}
        onRemove={remove}
      />
    ) : undefined;

  return {
    savedReportId: effectiveSavedReportId,
    saveControl: renderSaveControl(),
    renderSaveControl,
    markScheduled,
    markUnscheduled
  };
};
